import React from "react";
import { Link } from 'react-router-dom';
import { Navigation } from '../components/website/navigation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Footer } from '../components/website/footer';
// import { SmallHeader } from '../components/website/smallHeader';
// import JsonData from "../data/website_data.json";
// import { useState, useEffect } from "react";
// import axios from "axios";

function CalculatorHomePage() {
    const [headerRef, isHeaderVisible] = useScrollAnimation({ threshold: 0.2 });
    const [cardsRef, isCardsVisible] = useScrollAnimation({ threshold: 0.15 });

    return (
        <div className="bg-[#F3F3F3] min-h-screen">
            <Navigation />
            
            <div className="p-4 sm:px-6 lg:px-8 py-8 mt-16">
                {/* Header Card */}
                <div 
                    ref={headerRef}
                    className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm mb-16 transition-all duration-1000 ease-out transform ${
                        isHeaderVisible 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-4xl lg:text-5xl font-nunito font-bold text-black leading-tight mb-6">
                            Find the tool that fits your fleet best
                        </h1>
                        <p className="text-lg text-gray-600 font-nunito leading-relaxed max-w-2xl mx-auto">
                            Choose the calculator that matches your needs - from quick comparisons to comprehensive fleet analysis.
                        </p>
                    </div>
                </div>

                {/* Calculator Options Card */}
                <div 
                    ref={cardsRef}
                    className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                        isCardsVisible 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-8'
                    }`}
                >
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            {/* Basic Calculator Card */}
                            <div 
                                className={`bg-gray-50 rounded-xl p-8 h-full flex flex-col transition-all duration-1000 ease-out transform ${
                                    isCardsVisible 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-8'
                                }`}
                                style={{
                                    transitionDelay: isCardsVisible ? '200ms' : '0ms'
                                }}
                            >
                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-nunito font-bold text-black mb-4">
                                        Basic
                                    </h3>
                                    <div className="text-4xl font-nunito font-bold text-blue-600 mb-6">
                                        Free
                                    </div>
                                </div>
                                
                                <div className="flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Single vehicle comparison: Electric vs. Diesel side-by-side
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Customizable inputs: Vehicle types, incentives, interest rates, energy costs, and more
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Easy-to-use interface for quick TCO estimates
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                                
                                <div className="mt-8 pt-6">
                                    <Link 
                                        to="/fleet-planning-basic"
                                        className="btn-primary btn-lg btn-full"
                                    >
                                        Select Basic Calculator
                                    </Link>
                                </div>
                            </div>

                            {/* Pro Calculator Card */}
                            <div 
                                className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 h-full flex flex-col border-2 border-blue-200 relative overflow-hidden transition-all duration-1000 ease-out transform ${
                                    isCardsVisible 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-8'
                                }`}
                                style={{
                                    transitionDelay: isCardsVisible ? '400ms' : '0ms'
                                }}
                            >
                                {/* Popular Badge */}
                                <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                    Popular
                                </div>
                                
                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-nunito font-bold text-black mb-4">
                                        Pro
                                    </h3>
                                    <div className="text-lg font-nunito font-bold text-black mb-2">
                                        Starting at
                                    </div>
                                    <div className="text-4xl font-nunito font-bold text-blue-600">
                                        $49.99/month
                                    </div>
                                </div>
                                
                                <div className="flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Full fleet analysis: Leveraging your fleet trajectories or our trajectory database to provide a more accurate estimate
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Optimize savings: TOU rates, depot charging, solar, battery storage, and more
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Advanced planning: Integrate existing 3rd party charging infrastructure
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                                
                                <div className="mt-8 pt-6">
                                    <Link 
                                        to="/subscription"
                                        className="btn-primary btn-lg btn-full"
                                    >
                                        Select Pro Calculator
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}

export default CalculatorHomePage;